import styled from 'styled-components';

import ShowMoreText from '@libs/react-show-more-text';

import { Entity } from './types';

export const GalleryContainer = styled.div<{
  hotelFeatures?: boolean;
}>`
  height: 700px;
  min-width: 540px;
  max-width: ${(p) => (p.hotelFeatures ? '100%' : '920px')};
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_2X})`} {
    height: 100vh;
    max-height: 100%;
    width: 100%;
    border-radius: 0;
  }
  background-color: ${(p) => p.theme.palette.darkGrey};
  display: flex;
  justify-content: center;
  align-items: center;

  & img {
    object-fit: cover;
  }
`;

export const Details = styled.div`
  background-color: ${(p) => p.theme.palette.lynxWhite};
  height: 700px;
  display: flex;
  flex-direction: column;
  position: relative;
  @media ${({ theme }) => `(max-width: ${theme.breakPoint.LARGE_2X})`} {
    height: 100vh;
  }
  ${(p) => p.theme.mediaQueries.GALLERY_SMALL_SCREEN} {
    display: none;
  }
`;

export const DetailsHeader = styled.div`
  display: flex;
  position: relative;
  flex-direction: row-reverse;
  padding: 16px 16px 0 0;
  margin-bottom: 20px;
`;
export const Footer = styled.div`
  height: 77px;
  box-shadow: 0 -10px 20px 0 ${(p) => p.theme.palette.veryLightBlack};
  position: absolute;
  height: 77px;
  width: 100%;
  bottom: 0;
  background-color: ${(p) => p.theme.palette.white};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px 0 30px;
`;

export const FeaturesBlock = styled.div`
  height: calc(100% - 170px);
  overflow-y: scroll;
  padding-left: 30px;
  margin-right: 8px;
  &::-webkit-scrollbar {
    width: 5px;
    background-color: ${(p) => p.theme.palette.gray};
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 2.5px;
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: ${(p) => p.theme.palette.brownishGrey};
  }
`;

export const FooterRightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
`;
export const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
export const RoomDetails = styled.span`
  opacity: 0.75;
  font-size: 14px;
  font-weight: bold;
  color: ${(p) => p.theme.palette.darkGrey};
`;

export const ModalOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.5) !important;
`;

export const ModalContent = styled.div<{
  hotelFeatures?: boolean;
}>`
  width: 1440px;
  border-radius: 0;
  @media ${({ theme }) => `(min-width: ${theme.breakPoint.LARGE_2X})`} {
    border-radius: 8px;
    overflow: hidden;
  }
  ${(p) => p.theme.mediaQueries.GALLERY_SMALL_SCREEN} {
    max-width: 100%;
    height: 100%;
    ${GalleryContainer}, ${Details} {
      display: none;
    }
  }
  display: flex;
  max-width: 100vw;
  justify-content: center;
  align-items: center;
  .image-gallery-slide-wrapper.bottom {
    border-radius: 8px;
    overflow: hidden;
  }

  ${GalleryContainer} {
    flex: ${(p) => (p.hotelFeatures ? 1 : 0.64)};
  }
  ${Details} {
    flex: ${(p) => (p.hotelFeatures ? 0 : 0.36)};
    min-width: ${(p) => (p.hotelFeatures ? '0px' : '520px')};
  }
`;

export const FeatureGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
`;

export const FeatureGroupTitle = styled.span`
  font-size: 18px;
  font-weight: bold;
`;

export const FeatureGroupDescription = styled.p`
  font-size: 12px;
  line-height: 1.67;
  color: ${(p) => p.theme.palette.darkGray};
  padding-right: 20px;
`;

export const FeatureName = styled.span`
  font-size: 14px;
  color: ${(p) => p.theme.palette.darkGray};
  width: fit-content;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const Feature = styled.div`
  display: flex;
  gap: 5px;
  svg {
    width: 20px;
    height: 20px;
  }
  align-items: center;
`;

const ColumnsTemplate = {
  hotel: 'repeat(3, 1fr)',
  room: 'repeat(3, minmax(min-content, 1fr))',
};
export const FeatureListContainer = styled.div<{
  entityType: NonNullable<Entity>;
}>`
  display: grid;
  grid-template-columns: ${(p) => ColumnsTemplate[p.entityType]};
  grid-gap: 10px 20px;
  margin-top: 10px;
`;
export const FeatureLegend = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  ${FeatureName} {
    font-size: 12px;
    opacity: 0.75;
    font-weight: 600;
    line-height: normal;
  }
`;
export const FeatureGroupHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-right: 20px;
  margin-bottom: 10px;
`;

export const CustomShowMoreText = styled(ShowMoreText)`
  font-size: 12px;
  color: ${(p) => p.theme.palette.darkGray};
  line-height: 1.67;
`;

export const RoomDimension = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 5px;
  gap: 5px;
  & > span {
    font-size: 14px;
    line-height: 2.14;
    color: ${(p) => p.theme.palette.darkGray};
  }
`;

export const Bullet = styled.div`
  background-color: ${(p) => p.theme.palette.coral};
  width: 2px;
  height: 2px;
  border-radius: 50px;
`;
